/**
 * Owns the telemetry lifecycle for an authenticated session.
 *
 * Mounted once inside ProtectedLayout, so it starts when the user is signed in
 * and tears down when they are not. Renders nothing.
 *
 * Responsibilities:
 *   - run the idle gate and the session-level engaged timer
 *   - flush on tab-hidden and on pagehide (the close beacon)
 *   - send an idle-only heartbeat, so an active user sends none
 */

import { useEffect } from "react";

import { TELEMETRY_HEARTBEAT_IDLE_MS } from "src/constants/telemetry";

import { EngagedTimer, idleGate } from "./idleGate";
import { drainKpiHovers, endOpenVisit, settleKpiHovers } from "./index";
import { telemetryQueue } from "./queue";

export default function TelemetryProvider(): null {
  useEffect(() => {
    idleGate.start();
    const sessionEngaged = new EngagedTimer();

    // drain() rather than read(): the backend ADDS each delta, so reporting a
    // cumulative total would multiply engaged time on every flush.
    //
    // credit() is the counterpart: the drain has to happen before the request
    // (the value goes in the body), so if that request never reaches the server
    // the milliseconds are handed back instead of vanishing.
    //
    // The third argument drains completed KPI hovers on the queue's own ~10s
    // cadence, not just when this file's heartbeat happens to fire — see its
    // doc comment in queue.ts for why that split matters now that the
    // heartbeat below is genuinely idle-only.
    telemetryQueue.start(
      () => sessionEngaged.drain(),
      (ms) => sessionEngaged.credit(ms),
      drainKpiHovers,
    );
    telemetryQueue.resetCloseGuard();

    const flush = (closing = false) => {
      // Settle only when the page is going away. On an ordinary 10s flush an
      // in-progress hover must be left alone to keep accruing, or every hover
      // longer than the flush interval would be cut into pieces.
      if (closing) settleKpiHovers();
      drainKpiHovers();
      telemetryQueue.flush({ closing });
    };

    // Check in immediately, before waiting out a flush interval.
    //
    // A page RELOAD fires the same `pagehide` a real close does, so the session is
    // left marked `end_reason='closed'` — the beacon cannot tell the two apart and
    // marks optimistically. Only a NON-closing report clears that marker, and the
    // next one is up to a full interval away, so for those seconds a working user
    // reads as having closed their tab and their duration shows as frozen.
    //
    // `force` because the payload is empty at this point (nothing buffered, engaged
    // timer at zero), which an ordinary flush correctly short-circuits.
    telemetryQueue.flush({ force: true });

    // Idle-only heartbeat: fires solely to keep last_seen_at fresh for someone
    // READING a dashboard without interacting. A user who clicks around is
    // already generating events, so this never fires for them.
    //
    // Reads telemetryQueue.idleFor() rather than a locally-tracked timestamp:
    // this file used to track "last sent" itself, updated only by ITS OWN
    // calls -- so nothing about ordinary use (a click, a filter, the queue's
    // own periodic auto-flush) ever reset it, and this fired on a fixed
    // 5-minute clock for every user, active or not, regardless of how much
    // real activity was happening. idleFor() reflects ANY real send, from
    // anywhere, so this now only fires for someone genuinely idle.
    const heartbeat = window.setInterval(() => {
      if (telemetryQueue.idleFor() >= TELEMETRY_HEARTBEAT_IDLE_MS && document.visibilityState === "visible") {
        flush();
      }
    }, Math.max(30_000, TELEMETRY_HEARTBEAT_IDLE_MS / 5));

    // `visibilitychange`->hidden ALSO fires on a plain TAB SWITCH or window
    // minimize -- not just a real close -- and the browser cannot tell the two
    // apart from this event alone (see pagehide below, which can). This used to
    // flush with `closing: true` here too, on the reasoning that it lands
    // earlier and more reliably than pagehide for a REAL close. But that meant
    // every ordinary tab switch marked the session 'closed' in the admin portal
    // for however long until the person switched back and the next report
    // cleared it -- a real, if self-correcting, false read on every switch, not
    // just on an actual departure.
    //
    // So this now sends an ORDINARY report -- still worth doing promptly rather
    // than waiting for the next periodic tick, since a backgrounded tab can be
    // throttled or suspended by the OS -- but never marks 'closed'. Only
    // pagehide does that now, since it's the one signal that means the page is
    // actually going away rather than just not currently visible. Does NOT end
    // the open dashboard visit either: the visit continues when the user
    // returns -- DashboardPage does not remount, so an ended visit would never
    // restart and the rest of its time would vanish. The engaged timer already
    // pauses while hidden (idleGate.ts), so no idle time accrues meanwhile.
    const onHide = () => {
      if (document.visibilityState === "hidden") flush();
    };

    // `pagehide` means the page really is going away: tab closed, browser quit,
    // reload, or navigation away. The dashboard visit is therefore over and must
    // be closed HERE.
    //
    // Without this, `dashboard.dwell` was never emitted for whichever dashboard
    // was on screen at the end, so its time was silently lost -- a 30-minute
    // visit that ended in a close or reload reported nothing at all, while the
    // session's own engaged total still counted it. That mismatch is what made
    // the per-dashboard figures fail to add up to the session figure.
    //
    // Ordering matters: end the visit first so its dwell event is in the buffer,
    // then flush. The close guard zeroes a repeated engaged delta but never drops
    // buffered events, so the dwell still goes out.
    const onPageHide = () => {
      endOpenVisit();
      flush(true);
    };

    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", onPageHide);
      window.clearInterval(heartbeat);
      // Final flush on unmount (sign-out, or leaving the protected area) so the
      // last few events are not lost.
      settleKpiHovers();
      drainKpiHovers();
      telemetryQueue.flush();
      sessionEngaged.dispose();
      telemetryQueue.stop();
      idleGate.stop();
    };
  }, []);

  return null;
}
